import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, CalendarDays, Megaphone, Upload, UserCog, ListChecks,
  MessageSquare, CreditCard, ReceiptText, LifeBuoy, Settings, LogOut, Menu, X, Wand2, Rocket,
  Send, Plug, Gauge, TrendingUp, Camera, BookOpen,
} from "lucide-react";
import { Logo } from "../../components/site/Navbar";
import { useAuth } from "../../lib/AuthContext";
import BusinessNamePrompt from "../../components/BusinessNamePrompt";
import { useLang } from "../../lib/LangContext";

const items = [
  ["Home", "/dashboard", LayoutDashboard],
  ["Create", "/dashboard/create", Wand2],
  ["Photo To Post", "/dashboard/photo", Camera],
  ["How To Post", "/dashboard/how-to-post", BookOpen],
  ["Campaigns", "/dashboard/campaigns", Rocket],
  ["Content Calendar", "/dashboard/calendar", CalendarDays],
  ["My Content", "/dashboard/content", FileText],
  ["Publishing", "/dashboard/publishing", Send],
  ["Insights", "/dashboard/insights", TrendingUp],
  ["Connections", "/dashboard/connections", Plug],
  ["AutoPilot", "/dashboard/autopilot", Gauge],
  ["Promotions", "/dashboard/promotions", Megaphone],
  ["Media", "/dashboard/media", Upload],
  ["Brand", "/dashboard/brand", UserCog],
  ["My Tasks", "/dashboard/tasks", ListChecks],
  ["Requests", "/dashboard/requests", MessageSquare],
  ["Subscription", "/dashboard/subscription", CreditCard],
  ["Billing", "/dashboard/billing", ReceiptText],
  ["Support", "/dashboard/support", LifeBuoy],
  ["Settings", "/dashboard/settings", Settings],
];

export default function DashboardLayout() {
  const { tr } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const nav = (
    <nav className="flex flex-col gap-1" data-testid="dashboard-sidebar-nav">
      {items.map(([label, to, Icon]) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/dashboard"}
          onClick={() => setOpen(false)}
          data-testid={`sidebar-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`
          }
        >
          <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {tr(label)}
        </NavLink>
      ))}
      <button
        type="button"
        onClick={doLogout}
        data-testid="sidebar-logout-button"
        className="mt-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-red-50 hover:text-red-700"
      >
        <LogOut className="h-4 w-4" aria-hidden="true" />
        {tr("Logout")}
      </button>
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <BusinessNamePrompt />
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="flex items-center justify-between px-4 py-3 lg:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="dashboard-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link to="/" aria-label="PostMint home">
              <Logo />
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-600 sm:inline" data-testid="dashboard-user-label">
              {user?.business_name || user?.first_name}
            </span>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#F95738] text-sm font-bold text-white">
              {(user?.first_name || "U").charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
        {open && (
          <div className="border-t border-slate-200 bg-white px-4 py-3 lg:hidden" data-testid="dashboard-mobile-nav">
            {nav}
          </div>
        )}
      </header>

      <div className="mx-auto flex w-full max-w-[1500px] gap-8 px-4 py-6 lg:px-6">
        <aside className="hidden w-60 shrink-0 lg:block">
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-3 soft-shadow">{nav}</div>
        </aside>
        <main className="min-w-0 flex-1 pb-16">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
